import { Component, computed, effect, input, OnDestroy, signal } from '@angular/core';
import { lockPageScroll, unlockPageScroll } from '../../utils/scroll-lock.util';

/**
 * Interactive staged loading overlay - the same visual vocabulary as the
 * Workspace Adoption loader, reusable across every API-backed feature page.
 */
@Component({
  selector: 'app-interactive-loader',
  templateUrl: './interactive-loader.component.html',
  styleUrls: ['./interactive-loader.component.scss'],
  standalone: false,
})
export class InteractiveLoaderComponent implements OnDestroy {
  /** Main headline shown above the rotating stage message. */
  title = input<string>('Preparing your report');

  /** Stage messages cycled while the fetch is in flight. Pass a STABLE array reference. */
  stages = input<string[]>([]);

  /** Fixed stage text from the host (overrides internal stage cycling when set). */
  stageText = input<string>('');

  /** Milliseconds between stage rotations. */
  intervalMs = input<number>(1800);

  /** Number of shimmer skeleton rows under the progress track. */
  rows = input<number>(3);

  /** 'plain' single-line shimmer rows (default) or 'rich' avatar + line blocks. */
  rowVariant = input<'plain' | 'rich'>('plain');

  /** True (default) -> full-screen fixed overlay; false -> inline card. */
  overlay = input<boolean>(true);

  /** True -> freeze background page scroll while a full-screen overlay is up. */
  lockScroll = input<boolean>(false);

  /** True -> show a "Part X of Y" progress bar instead of the indeterminate track. */
  showProgress = input<boolean>(false);
  progressPart = input<number>(0);
  progressTotal = input<number>(1);

  /** True -> "background sync" mode: adds a syncing badge to the stage row. */
  background = input<boolean>(false);

  private readonly stageIndex = signal(0);
  private timer: ReturnType<typeof setInterval> | null = null;

  readonly displayStage = computed<string>(() => {
    const fixed = this.stageText();
    if (fixed) return fixed;
    const stages = this.stages();
    if (!stages.length) return 'Working…';
    return stages[this.stageIndex() % stages.length];
  });

  readonly fillPct = computed<number>(() => {
    const total = Math.max(1, this.progressTotal());
    const part = Math.max(0, this.progressPart());
    return Math.min(100, Math.round((part / total) * 100));
  });

  readonly rowIndexes = computed<number[]>(() =>
    Array.from({ length: Math.max(1, this.rows()) }, (_, i) => i),
  );

  /**
   * Freezes page scroll for as long as a full-screen overlay is up. The cleanup
   * runs on input change and on destroy, so the scroll position is always
   * restored exactly once.
   */
  private readonly scrollLock = effect((onCleanup) => {
    if (!this.lockScroll() || !this.overlay()) return;
    lockPageScroll();
    onCleanup(unlockPageScroll);
  });

  constructor() {
    // (Re)start the rotation whenever the stage list / fixed text changes so a
    // new fetch flow always begins on its first message.
    effect(() => {
      this.stages();
      this.stageText();
      this.startTimer();
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private startTimer(): void {
    this.stopTimer();
    this.stageIndex.set(0);
    if (this.stages().length > 1 && !this.stageText()) {
      this.timer = setInterval(
        () => this.stageIndex.update(s => (s + 1) % this.stages().length),
        this.intervalMs(),
      );
    }
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}